import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { renderMarkdown } from '../../../utils/markdown';
import AdminService from '../../../API/AdminService';
import styles from './ArticleEditor.module.css';
import { ADMIN_addres } from '../../../API/API';
import logger from '../../../utils/logger';
const ArticleEditor = () => {
    const navigate = useNavigate();
    const { articleId } = useParams();
    const isEditing = !!articleId;
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        summary: '',
        tags: '',
        difficulty_level: 1,
        featured_image: '',
        is_published: true
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [imageUploading, setImageUploading] = useState(false);
    const [popularTags, setPopularTags] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    // Парсинг markdown вынесен в src/utils/markdown.js — там настроен
    // строгий DOMPurify whitelist и единая конфигурация marked.
    const parseMarkdown = renderMarkdown;

    useEffect(() => {
        loadPopularTags();
        if (isEditing) {
            loadArticleForEdit();
        }
    }, [articleId]);

    const loadPopularTags = async () => {
        try {
            const response = await AdminService.getPopularTags(20);
            setPopularTags(response.data || []);
        } catch (err) {
            logger.error('Ошибка загрузки тегов:', err);
        }
    };

    const loadArticleForEdit = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getArticle(articleId);
            const article = response.data;
            
            setFormData({
                title: article.title,
                content: article.content,
                summary: article.summary || '',
                tags: article.tags ? article.tags.map(tag => tag.name).join(', ') : '',
                difficulty_level: article.difficulty_level,
                featured_image: article.featured_image || '',
                is_published: article.is_published
            });
        } catch (err) {
            logger.error('Ошибка загрузки статьи:', err);
            setErrors(['Ошибка загрузки статьи для редактирования']);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors.length > 0) {
            setErrors([]);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Размер файла не должен превышать 5MB');
            return;
        }

        try {
            setImageUploading(true);
            const response = await AdminService.uploadArticleImage(file);
            
            
            const fullImageUrl = `${ADMIN_addres}${response.image_url}`;
            
            const textarea = document.querySelector('.editor-content');
            const imageMarkdown = `\n![${file.name}](${fullImageUrl})\n`;
            
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newContent = formData.content.substring(0, start) + 
                                 imageMarkdown + 
                                 formData.content.substring(end);
                
                setFormData(prev => ({
                    ...prev,
                    content: newContent
                }));
                
                setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + imageMarkdown.length;
                    textarea.focus();
                }, 0);
            }
        } catch (error) {
            logger.error('Ошибка загрузки изображения:', error);
            alert('Ошибка загрузки изображения');
        } finally {
            setImageUploading(false);
        }
    };

    const insertImageAtCursor = () => {
        fileInputRef.current?.click();
    };

    const insertFormatting = (format) => {
        const textarea = document.querySelector('.editor-content');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = formData.content.substring(start, end);
        let replacement = '';

        switch (format) {
            case 'bold':
                replacement = `**${selectedText || 'жирный текст'}**`;
                break;
            case 'italic':
                replacement = `*${selectedText || 'курсив'}*`;
                break;
            case 'heading':
                replacement = `## ${selectedText || 'Заголовок'}`;
                break;
            case 'link':
                replacement = `[${selectedText || 'текст ссылки'}](URL)`;
                break;
            case 'list':
                replacement = `\n- ${selectedText || 'пункт списка'}\n- пункт 2\n- пункт 3\n`;
                break;
            case 'quote':
                replacement = `> ${selectedText || 'цитата'}`;
                break;
            case 'code':
                replacement = `\`${selectedText || 'код'}\``;
                break;
            default:
                return;
        }

        const newContent = formData.content.substring(0, start) + 
                          replacement + 
                          formData.content.substring(end);

        setFormData(prev => ({
            ...prev,
            content: newContent
        }));

        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = start + replacement.length;
        }, 0);
    };

    const handleTagClick = (tagName) => {
        const currentTags = formData.tags ? formData.tags.split(',').map(t => t.trim()) : [];
        if (currentTags.includes(tagName)) {
            const newTags = currentTags.filter(t => t !== tagName);
            setFormData(prev => ({
                ...prev,
                tags: newTags.join(', ')
            }));
        } else {
            const newTags = [...currentTags, tagName].filter(t => t);
            setFormData(prev => ({
                ...prev,
                tags: newTags.join(', ')
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const articleData = {
            ...formData,
            tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
        };

        const validationErrors = AdminService.validateArticleData(articleData);
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);
            
            if (isEditing) {
                await AdminService.updateArticle(articleId, articleData);
            } else {
                await AdminService.createArticle(articleData);
            }

            navigate('/main/articles/my');
        } catch (error) {
            logger.error('Ошибка сохранения статьи:', error);
            setErrors([isEditing ? 'Ошибка обновления статьи' : 'Ошибка создания статьи']);
        } finally {
            setLoading(false);
        }
    };

    const currentTags = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [];

    if (loading && isEditing) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Загрузка статьи...</p>
            </div>
        );
    }

    return (
        <div className={styles.editorContainer}>
            {/* Заголовок */}
            <div className={styles.header}>
                <button 
                    onClick={() => navigate('/main/articles/my')}
                    className={styles.backButton}
                >
                    ← Назад к моим статьям
                </button>
                <h1>{isEditing ? 'Редактировать статью' : 'Написать новую статью'}</h1>
            </div>

            <form onSubmit={handleSubmit} className={styles.editorForm}>
                {/* Ошибки */}
                {errors.length > 0 && (
                    <div className={styles.errorMessages}>
                        {errors.map((error, index) => (
                            <p key={index} className={styles.errorMessage}>{error}</p>
                        ))}
                    </div>
                )}

                <div className={styles.formLayout}>
                    <div className={styles.mainEditor}>
                        {/* Заголовок статьи */}
                        <div className={styles.formGroup}>
                            <label htmlFor="title">Заголовок статьи*</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Введите заголовок статьи"
                                maxLength="200"
                                required
                                className={styles.titleInput}
                            />
                            <small className={styles.charCounter}>
                                {formData.title.length}/200 символов
                            </small>
                        </div>

                        {/* Краткое описание */}
                        <div className={styles.formGroup}>
                            <label htmlFor="summary">Краткое описание</label>
                            <textarea
                                id="summary"
                                name="summary"
                                value={formData.summary}
                                onChange={handleInputChange}
                                placeholder="Краткое описание статьи для превью"
                                rows="3"
                                maxLength="500"
                                className={styles.summaryInput}
                            />
                            <small className={styles.charCounter}>
                                {formData.summary.length}/500 символов
                            </small>
                        </div>

                        {/* Панель инструментов редактора */}
                        <div className={styles.editorToolbar}>
                            <div className={styles.toolbarGroup}>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('bold')}
                                    className={styles.toolButton}
                                    title="Жирный текст"
                                >
                                    <strong>B</strong>
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('italic')}
                                    className={styles.toolButton}
                                    title="Курсив"
                                >
                                    <em>I</em>
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('heading')}
                                    className={styles.toolButton}
                                    title="Заголовок"
                                >
                                    H2
                                </button>
                            </div>

                            <div className={styles.toolbarGroup}>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('link')}
                                    className={styles.toolButton}
                                    title="Ссылка"
                                >
                                    🔗
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('list')}
                                    className={styles.toolButton}
                                    title="Список"
                                >
                                    📝
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('quote')}
                                    className={styles.toolButton}
                                    title="Цитата"
                                >
                                    💬
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => insertFormatting('code')}
                                    className={styles.toolButton}
                                    title="Код"
                                >
                                    &lt;/&gt;
                                </button>
                            </div>

                            <div className={styles.toolbarGroup}>
                                <button 
                                    type="button" 
                                    onClick={insertImageAtCursor}
                                    disabled={imageUploading}
                                    className={styles.imageButton}
                                    title="Вставить изображение"
                                >
                                    {imageUploading ? '⏳' : '🖼️'} Изображение
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />
                            </div>

                            <div className={styles.toolbarGroup}>
                                <button 
                                    type="button" 
                                    onClick={() => setShowPreview(!showPreview)}
                                    className={`${styles.toolButton} ${showPreview ? styles.active : ''}`}
                                    title="Предварительный просмотр"
                                >
                                    👁️ Превью
                                </button>
                            </div>
                        </div>

                        {/* Редактор содержимого */}
                        <div className={styles.formGroup}>
                            <label htmlFor="content">Содержание статьи*</label>
                            {showPreview ? (
                                <div className={styles.previewContainer}>
                                    <div 
                                        className={styles.preview}
                                        dangerouslySetInnerHTML={{ 
                                            __html: parseMarkdown(formData.content) 
                                        }}
                                    />
                                </div>
                            ) : (
                                <textarea
                                    id="content"
                                    name="content"
                                    value={formData.content}
                                    onChange={handleInputChange}
                                    placeholder="Напишите содержание вашей статьи здесь..."
                                    rows="20"
                                    required
                                    className={`${styles.contentTextarea} editor-content`}
                                />
                            )}
                        </div>
                    </div>

                    {/* Боковая панель */}
                    <div className={styles.sidebar}>
                        {/* Настройки статьи */}
                        <div className={styles.sidebarSection}>
                            <h3>Настройки</h3>
                            
                            <div className={styles.formGroup}>
                                <label htmlFor="difficulty_level">Уровень сложности</label>
                                <select
                                    id="difficulty_level"
                                    name="difficulty_level"
                                    value={formData.difficulty_level}
                                    onChange={handleInputChange}
                                    className={styles.selectInput}
                                >
                                    <option value={1}>Начинающий</option>
                                    <option value={2}>Легкий</option>
                                    <option value={3}>Средний</option>
                                    <option value={4}>Продвинутый</option>
                                    <option value={5}>Экспертный</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="featured_image">Обложка статьи (URL)</label>
                                <input
                                    type="url"
                                    id="featured_image"
                                    name="featured_image"
                                    value={formData.featured_image}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com/image.jpg"
                                    className={styles.input}
                                />
                            </div>

                            {/* <div className={styles.checkboxGroup}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="is_published"
                                        checked={formData.is_published}
                                        onChange={handleInputChange}
                                    />
                                    <span className={styles.checkmark}></span>
                                    Опубликовать статью
                                </label>
                            </div> */}
                        </div>

                        {/* Теги */}
                        <div className={styles.sidebarSection}>
                            <h3>Теги</h3>
                            
                            <div className={styles.formGroup}>
                                <label htmlFor="tags">Теги (через запятую)</label>
                                <input
                                    type="text"
                                    id="tags"
                                    name="tags"
                                    value={formData.tags}
                                    onChange={handleInputChange}
                                    placeholder="маркетинг, управление, финансы"
                                    className={styles.input}
                                />
                            </div>

                            {/* Текущие теги */}
                            {currentTags.length > 0 && (
                                <div className={styles.currentTags}>
                                    <span>Текущие теги:</span>
                                    <div className={styles.tagsList}>
                                        {currentTags.map((tag, index) => (
                                            <span key={index} className={styles.currentTag}>
                                                #{tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleTagClick(tag)}
                                                    className={styles.removeTag}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Популярные теги */}
                            {popularTags.length > 0 && (
                                <div className={styles.popularTags}>
                                    <span>Популярные теги:</span>
                                    <div className={styles.tagsList}>
                                        {popularTags.slice(0, 15).map(tag => (
                                            <button
                                                key={tag.id}
                                                type="button"
                                                onClick={() => handleTagClick(tag.name)}
                                                className={`${styles.popularTag} ${
                                                    currentTags.includes(tag.name) ? styles.selected : ''
                                                }`}
                                            >
                                                #{tag.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Кнопки действий */}
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        onClick={() => navigate('/main/articles/my')}
                        className={styles.cancelButton}
                        disabled={loading}
                    >
                        Отмена
                    </button>
                    <button 
                        type="submit"
                        className={styles.submitButton}
                        disabled={loading || !formData.title.trim() || !formData.content.trim()}
                    >
                        {loading 
                            ? (isEditing ? 'Сохраняю...' : 'Создаю...') 
                            : (isEditing ? 'Сохранить изменения' : 'Создать статью')
                        }
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ArticleEditor;